/**
 * Cart Drawer
 * Handles drawer open/close and content updates
 */

import { getDrawerContent, updateCartItemQuantity, removeCartItem, removeCoupon, getCart } from './api'
import { OverlayScrollbars } from 'overlayscrollbars'

let scrollInstance = null

/**
 * Initialize drawer event handlers
 */
export function initializeDrawerEvents() {
  // Drawer close button
  document.addEventListener('click', (e) => {
    if (e.target.matches('#td-drawer-close, #td-drawer-close *')) {
      hideDrawer()
    }
  })

  // Drawer shadow click to close
  document.addEventListener('click', (e) => {
    if (e.target.matches('.td-drawer-shadow')) {
      hideDrawer()
    }
  })

  // Cart icon click to show drawer
  document.addEventListener('click', (e) => {
    if (e.target.matches('.site-header__tools--cart--button, .site-header__tools--cart--button *, .site-header__cart--button, .site-header__cart--button *')) {
      e.preventDefault()
      showDrawer()
    }
  })

  // Remove item buttons
  document.addEventListener('click', handleRemoveItem)

  // Recalculate scroll area on window resize
  let resizeTimeout
  window.addEventListener('resize', () => {
    const drawer = document.querySelector('.td-drawer.active')
    if (drawer) {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
          initializeScrollArea()
        })
      }, 100)
    }
  })
}

/**
 * Show drawer
 */
export async function showDrawer() {
  if (shouldSkipDrawer()) return

  const { drawer, drawerShadow } = getDrawerElements()
  if (!drawer || !drawerShadow) return

  openDrawer(drawer, drawerShadow)
  await updateDrawer()
}

/**
 * Show drawer with cart data (optimized - no extra API call)
 */
export async function showDrawerWithData(cartData) {
  if (shouldSkipDrawer()) return

  const { drawer, drawerShadow } = getDrawerElements()
  if (!drawer || !drawerShadow) return

  openDrawer(drawer, drawerShadow)
  await updateDrawerContent()
}

/**
 * Hide drawer
 */
export function hideDrawer() {
  const { drawer, drawerShadow } = getDrawerElements()

  if (drawer && drawerShadow) {
    drawer.classList.remove('active')
    drawerShadow.classList.remove('active')
    document.body.classList.remove('noscroll')
    document.body.style.overflow = 'auto'
  }

  // Destroy OverlayScrollbars instance
  if (scrollInstance) {
    scrollInstance.destroy()
    scrollInstance = null
  }
}

/**
 * Update drawer content from API
 */
export async function updateDrawer() {
  const drawerContent = document.querySelector('#td-drawer-content')
  const drawerLoading = document.querySelector('.td-drawer .loading')

  if (!drawerContent) return

  setLoadingState(drawerLoading, true)

  try {
    await updateDrawerContent()
  } catch (error) {
    console.error('Error updating drawer:', error)
  } finally {
    setLoadingState(drawerLoading, false)
  }
}

/**
 * Update drawer content
 */
async function updateDrawerContent() {
  const drawerContent = document.querySelector('#td-drawer-content')
  if (!drawerContent) return

  const drawerData = await getDrawerContent()
  drawerContent.innerHTML = drawerData.html

  // Update cart icon
  const cartIconContainer = document.querySelector('.site-header__tools--cart, .site-header__cart')
  if (cartIconContainer && drawerData.cart_icon_html) {
    cartIconContainer.innerHTML = drawerData.cart_icon_html
  }

  // Calculate scroll height and initialize OverlayScrollbars
  // Small delay to ensure DOM is fully rendered
  requestAnimationFrame(() => {
    initializeScrollArea()
  })
}

/**
 * Handle remove item click
 */
async function handleRemoveItem(e) {
  if (!e.target.matches('.delete, .delete *')) return

  e.preventDefault()

  const button = e.target.closest('.delete')
  const cartItemKey = button?.dataset.cartItemKey
  const removeCoupons = button?.dataset.coupons === 'true'

  if (!cartItemKey) return

  const drawerLoading = document.querySelector('.td-drawer .loading')
  setLoadingState(drawerLoading, true)

  try {
    await removeCartItem(cartItemKey)

    // Handle coupon removal if needed
    if (removeCoupons) {
      const cart = await getCart()
      for (const coupon of cart.coupons || []) {
        await removeCoupon(coupon.code)
      }
    }

    await updateDrawer()
  } catch (error) {
    console.error('Error removing cart item:', error)
    setLoadingState(drawerLoading, false)
  }
}

/**
 * Get drawer DOM elements
 */
function getDrawerElements() {
  return {
    drawer: document.querySelector('.td-drawer'),
    drawerShadow: document.querySelector('.td-drawer-shadow')
  }
}

/**
 * Check if drawer should be skipped (cart/checkout pages)
 */
function shouldSkipDrawer() {
  return document.querySelector('.td-woocommerce-cart, .td-woocommerce-checkout')
}

/**
 * Open drawer
 */
function openDrawer(drawer, drawerShadow) {
  document.body.classList.add('noscroll')
  drawer.classList.add('active')
  drawerShadow.classList.add('active')
  document.body.style.overflow = 'hidden'
}

/**
 * Set loading state
 */
function setLoadingState(loadingElement, isLoading) {
  if (!loadingElement) return

  if (isLoading) {
    loadingElement.classList.add('active')
  } else {
    loadingElement.classList.remove('active')
  }
}

/**
 * Calculate scroll area height and initialize OverlayScrollbars
 */
function initializeScrollArea() {
  const drawer = document.querySelector('.td-drawer')
  const drawerContent = document.querySelector('#td-drawer-content')
  const scrollArea = drawerContent?.querySelector('.scroll')

  if (!drawer || !drawerContent || !scrollArea) return

  // Destroy previous instance if exists
  if (scrollInstance) {
    scrollInstance.destroy()
    scrollInstance = null
  }

  // Get total available height from .td-drawer
  const totalHeight = drawer.clientHeight

  // Calculate total height of all siblings to .scroll
  let siblingsHeight = 0

  // Loop through all direct children of #td-drawer-content
  Array.from(drawerContent.children).forEach((child, index) => {
    // Skip the scroll area itself
    if (child === scrollArea) return

    // Get element height including margins
    const styles = window.getComputedStyle(child)
    const marginTop = parseFloat(styles.marginTop) || 0
    const marginBottom = parseFloat(styles.marginBottom) || 0
    const height = child.offsetHeight

    siblingsHeight += height + marginTop + marginBottom
  })

  // Account for scroll area's own margins
  const scrollStyles = window.getComputedStyle(scrollArea)
  const scrollMarginTop = parseFloat(scrollStyles.marginTop) || 0
  const scrollMarginBottom = parseFloat(scrollStyles.marginBottom) || 0

  // Calculate remaining height for scroll area
  const scrollHeight = Math.max(100, totalHeight - siblingsHeight - scrollMarginTop - scrollMarginBottom)

  // Set the height for scroll area
  scrollArea.style.height = `${scrollHeight}px`

  // Initialize OverlayScrollbars on the scroll area
  scrollInstance = OverlayScrollbars(scrollArea, {
    scrollbars: {
      autoHide: 'never',
    },
    overflow: {
      x: 'hidden',
      y: 'scroll'
    }
  })
}
